import AsyncSelect from "react-select/async";
import { listPrimeCompanies } from "../api/CrmApi";

export interface CompanyOption {
  value: number;
  label: string;
  domain: string | null;
}

interface Props {
  value: CompanyOption | null;
  onChange: (option: CompanyOption | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
  inputId?: string;
}

const STYLES = {
  control: (base: any, state: any) => ({
    ...base,
    background: "hsl(var(--surface))",
    borderColor: state.isFocused ? "hsl(var(--primary))" : "hsl(var(--border))",
    borderRadius: "0.75rem",
    minHeight: "38px",
    boxShadow: "none",
    fontSize: "0.875rem",
    color: "hsl(var(--foreground))",
    "&:hover": { borderColor: "hsl(var(--primary))" },
  }),
  // above the Modal overlay (z-[999999]) the picker lives in, or the menu opens underneath it
  menuPortal: (base: any) => ({ ...base, zIndex: 1000001 }),
  menu: (base: any) => ({
    ...base,
    background: "hsl(var(--surface-muted))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "0.75rem",
    overflow: "hidden",
  }),
  menuList: (base: any) => ({ ...base, padding: "4px" }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected ? "hsl(var(--primary))" : state.isFocused ? "hsl(var(--surface))" : "transparent",
    color: state.isSelected ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
    fontSize: "0.875rem",
    borderRadius: "0.5rem",
    padding: "6px 10px",
    cursor: "pointer",
  }),
  singleValue: (base: any) => ({ ...base, color: "hsl(var(--foreground))" }),
  input: (base: any) => ({ ...base, color: "hsl(var(--foreground))" }),
  placeholder: (base: any) => ({ ...base, color: "hsl(var(--muted-foreground))" }),
  noOptionsMessage: (base: any) => ({ ...base, color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }),
};

async function loadCompanies(input: string): Promise<CompanyOption[]> {
  const res = await listPrimeCompanies({ search: input.trim() || undefined, page_size: 20 });
  return res.items.map((c) => ({ value: c.id, label: c.name || c.domain || `Company #${c.id}`, domain: c.domain }));
}

/** Search-as-you-type company chooser (the existing companies search, 20 at a time). */
export default function CompanyPicker({ value, onChange, placeholder = "Search companies by name or domain…", autoFocus, inputId }: Props) {
  return (
    <AsyncSelect<CompanyOption, false>
      value={value}
      onChange={(option) => onChange(option)}
      loadOptions={loadCompanies}
      defaultOptions
      cacheOptions
      isClearable
      autoFocus={autoFocus}
      inputId={inputId}
      aria-label="Company"
      placeholder={placeholder}
      noOptionsMessage={({ inputValue }) => (inputValue ? "No company matches" : "No companies yet")}
      formatOptionLabel={(option) => (
        <span>
          {option.label}
          {option.domain && <span className="text-muted-foreground"> · {option.domain}</span>}
        </span>
      )}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      styles={STYLES}
    />
  );
}
